import { LitElement } from 'lit';
import { fromEvent, tap } from 'rxjs';

class ToujouSolrSearch extends LitElement {
  static get is() {
    return 'toujou-solr-search';
  }

  static get properties() {
    return {
      isFilterOpen: { type: Boolean },
      isLoading: { type: Boolean },
      ajaxPageType: { type: Number, attribute: 'ajax-page-type' },
      viewMode: {
        type: String,
        reflect: true,
        attribute: 'view-mode',
      },
      elementPartsSelectors: {
        type: Array,
        attribute: 'element-parts-selector',
      },
    };
  }

  constructor() {
    super();
    this.ajaxPageType = 1632;
    this.searchBreakpoint = null;
    this.isFilterOpen = false;
    this.isLoading = false;
    this.viewMode = null;

    this.elementPartsSelectors = [
      '.solr-filters',
      '.solr-header',
      '.solr-search',
      '.solr-pagination--above',
      '.solr-pagination--below',
      '.solr-pagination--load-more',
      '.item-list--solr',
      '.solr-results__header',
    ];
  }

  connectedCallback() {
    super.connectedCallback();

    const computedStyles = getComputedStyle(this);
    const searchBreakPointValue = computedStyles.getPropertyValue('--toujou-solr-search-filters-breakpoint');
    this.searchBreakpoint = window.matchMedia(`(min-width: ${searchBreakPointValue})`);
    this.init();

    this.facadeValueChangedSubscription = fromEvent(this, 'toujou-solr-search-facade-value-changed').pipe(
      tap(event => this.performSearch(event.detail.url)),
    ).subscribe();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.facadeValueChangedSubscription.unsubscribe();
  }

  init() {
    this.initAjaxSearchFields();
    this.initSearchCancelButton();
    this.initAjaxForms();
    this.initFilterModal();
    this.initSlider();
    if (this.viewModeButtons.length > 0) {
      this.initViewModeButtons();
      this.updateViewMode();
    }
  }

  initAjaxSearchFields() {
    const ajaxClickElements = this.querySelectorAll('.solr-ajaxified');
    ajaxClickElements.forEach((elem) => {
      elem.addEventListener('click', (e) => {
        const url = elem.hasAttribute('href')
          ? elem.getAttribute('href')
          : elem.getAttribute('data-url');

        if (url === null) {
          return;
        }
        if (elem.nodeName === 'A') {
          e.preventDefault();
        }

        const appendResult = elem.classList.contains('solr-ajaxified--load-more');

        this.performSearch(url, appendResult);
      });
    });
  }

  initAjaxForms() {
    const ajaxForms = this.querySelectorAll('.solr-ajaxified-form');
    ajaxForms.forEach((form) => {
      form.addEventListener('change', () => {
        form.querySelectorAll('[data-select-facet]').forEach((element) => {
          if (element.value === '') {
            element.setAttribute('disabled', '');
          }
        });

        const formData = new FormData(form);
        const params = new URLSearchParams(formData);
        const formUrl = window.location.toString().replace(window.location.search, '');
        const url = formUrl + (formUrl.indexOf('?') !== -1 ? '&' : '?') + params.toString();
        this.performSearch(url);
      });
    });
  }

  initSearchCancelButton() {
    const searchInput = this.querySelector('.solr-search__input');
    const searchForm = this.querySelector('.solr-search__form');
    const cancelButton = this.querySelector('.solr-search__input-cancel-button');

    if (cancelButton === null) {
      return;
    }

    cancelButton.addEventListener('click', () => {
      cancelButton.blur();
      if (searchInput.value !== '') {
        searchInput.value = '';
        searchForm.submit();
      }
    });
  }

  initFilterModal() {
    /** @type HTMLButtonElement * */
    const filterButton = this.querySelector('.solr-filter-button');
    const closeFilterModalButton = this.querySelector('.solr-filters__mobile-button--close');

    if (filterButton == null) {
      return;
    }

    filterButton.addEventListener('click', () => {
      filterButton.blur();
      this.isFilterOpen = !this.isFilterOpen;
      this._updateFilterUI();
    });

    closeFilterModalButton.addEventListener('click', () => {
      closeFilterModalButton.blur();
      this.isFilterOpen = false;
      this._updateFilterUI();
    });

    // listen to resize. When we cross the breakpoint, close the filters
    this.searchBreakpoint.addEventListener('change', (event) => {
      if (event.matches) {
        this.isFilterOpen = false;
        this._updateFilterUI();
      }
    });
  }

  /**
   * @deprecated should be done via markup
   */
  initSlider() {
    const solrCardsSlider = this.querySelector('.slider--solr-cards');
    if (solrCardsSlider === null) {
      return;
    }

    const sliderReadyCallback = () => {
      const solrCardsSliderGlider = solrCardsSlider.querySelector('.glider');
      if (!solrCardsSliderGlider || !solrCardsSliderGlider._glider) return;

      const solrCardsSliderOptions = {
        slidesToShow: 1,
        slidesToScroll: 1,
        draggable: true,
        scrollLock: true,
        resizeLock: true,
        rewind: true,
        arrows: {
          prev: solrCardsSlider.querySelector(':scope > .slider__control--prev'),
          next: solrCardsSlider.querySelector(':scope > .slider__control--next'),
        },
        responsive: [
          {
            breakpoint: 480,
            settings: { slidesToShow: 2 },
          },
          {
            breakpoint: 840,
            settings: { slidesToShow: 3 },
          },
          {
            breakpoint: 1020,
            settings: { slidesToShow: 4 },
          },
        ],
      };
      solrCardsSliderGlider._glider.setOption(solrCardsSliderOptions);
      solrCardsSliderGlider._glider.refresh(true);
    };

    if (solrCardsSlider.querySelector('.glider')) {
      sliderReadyCallback();
    } else {
      solrCardsSlider.addEventListener(
        'glider-loaded',
        sliderReadyCallback, { once: true, passive: true },
      );
    }
  }

  /**
   * @param {string} url
   * @param {boolean} appendResult
   */
  performSearch(url, appendResult = false) {
    if (this.isLoading === true) {
      return;
    }

    const urlObject = new URL(url, document.baseURI);
    urlObject.searchParams.append('type', this.ajaxPageType.toString(10));
    const loader = document.createElement('toujou-spinner');
    loader.setAttribute('centered-on-page', '');
    this.appendChild(loader);
    this.isLoading = true;

    const eventResult = this.dispatchEvent(new CustomEvent('toujou-solr-search-before-search', {
      bubbles: true,
      cancelable: true,
      detail: urlObject,
    }));

    if (eventResult === false) {
      return;
    }

    fetch(urlObject.toString())
      .then(response => response.text())
      .then((html) => {
        const dummyElement = document.createElement('div');
        dummyElement.innerHTML = html;

        return dummyElement;
      })
      .then((htmlElement) => {
        for (const partSelector of this.elementPartsSelectors) {
          try {
            const content = htmlElement.querySelector(partSelector)?.innerHTML;

            if (appendResult && partSelector === '.item-list--solr') {
              this.querySelector(partSelector).innerHTML += content;
            } else {
              this.querySelector(partSelector).innerHTML = content ?? '';
            }
          } catch (e) {
            // eslint-disable-next-line no-console
            console.debug(`element ${partSelector} not found in solr container ${e}`);
          }
        }
      })
      .then(() => {
        if (appendResult === false) {
          urlObject.searchParams.delete('type');
          if (this.viewMode) {
            urlObject.searchParams.append('viewMode', this.viewMode);
          }
          window.history.replaceState({}, null, urlObject.toString());
        }
      })
      .catch((e) => {
        // @todo proper logging
        // eslint-disable-next-line no-console
        console.log(e);
      })
      .finally(() => {
        loader.remove();
        this.isLoading = false;
        this.isFilterOpen = false;
        this._updateFilterUI();
        this.init();

        window.dispatchEvent(new CustomEvent('toujou-solr-search-new-ajax-result', {
          bubbles: true,
        }));

        if (appendResult !== true) {
          this._scrollToResultsTop();
        }
      });
  }

  createRenderRoot() {
    return this;
  }

  _updateFilterUI() {
    /** @type HTMLElement * */
    const filterContainer = this.querySelector('.solr-filters');
    if (!filterContainer) {
      return;
    }

    if (this.isFilterOpen) {
      filterContainer.setAttribute('is-visible', '');
      document.body.setAttribute('solr-search-filter-open', '');
    } else {
      filterContainer.removeAttribute('is-visible');
      document.body.removeAttribute('solr-search-filter-open');
    }
  }

  _scrollToResultsTop() {
    const resultsElement = this.querySelector('.solr-results');
    if (resultsElement) {
      resultsElement.scrollIntoView();
    }
  }

  initViewModeButtons() {
    this.viewModeButtons.forEach(button => button.addEventListener('click', () => {
      const mode = button.dataset.viewMode;
      const urlObject = new URL(document.location);
      urlObject.searchParams.set('viewMode', mode);
      window.history.replaceState({}, null, urlObject.toString());
      this.updateViewMode();
    }));
  }

  updateViewMode() {
    const urlObject = new URL(document.location);
    if (urlObject.searchParams.has('viewMode')) {
      this.viewMode = urlObject.searchParams.get('viewMode');
    } else {
      this.viewMode = this.viewModeButtons[0].dataset.viewMode;
    }
    this.viewModeButtons.forEach((button) => {
      const buttonMode = button.dataset.viewMode;
      button.setAttribute('aria-checked', buttonMode === this.viewMode ? 'true' : 'false');
      button.setAttribute('tabindex', buttonMode === this.viewMode ? '0' : '-1');
    });
  }

  /**
   * @return {NodeListOf<HTMLElement>}
   */
  get viewModeButtons() {
    return this.querySelectorAll('.solr-view-mode__button');
  }
}

customElements.define(ToujouSolrSearch.is, ToujouSolrSearch);
